import { api } from '../lib/api'

/**
 * Wrapper para normalizar respuestas de la API
 * Maneja los diferentes formatos: { data: ... }, { message: ... }, array directo, objeto directo
 * 
 * @param {any} response - Respuesta de la API
 * @returns {any} - Datos normalizados
 */
function unwrap(response) {
  // Si la respuesta es null o undefined, retornar tal cual
  if (response == null) {
    return response
  }

  // Si tiene res.data?.data, usar eso (formato anidado)
  if (response?.data?.data !== undefined) {
    return response.data.data
  }

  // Si tiene res.data, usar eso (formato estándar)
  if (response?.data !== undefined) {
    return response.data
  }

  // Si no tiene .data, retornar la respuesta directa (array u objeto)
  return response
}

/** @param {Record<string, unknown>|null|undefined} order */
function parseOrderStartedAt(order) {
  if (!order || typeof order !== 'object') return null
  const raw =
    order.StarteddAt ??
    order.StartedAt ??
    order.starteddAt ??
    order.started_at ??
    order.startedAt
  if (raw == null || raw === '') return null
  const d = raw instanceof Date ? raw : new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/** @param {Record<string, unknown>|null|undefined} order */
function parseOrderFinishedAt(order) {
  if (!order || typeof order !== 'object') return null
  const raw = order.FinishedAt ?? order.finished_at ?? order.finishedAt
  if (raw == null || raw === '') return null
  const d = raw instanceof Date ? raw : new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Servicio para manejar todas las operaciones relacionadas con períodos y consumos
 * Normaliza las respuestas de la API para que el componente React no tenga que lidiar con formatos distintos
 */
class SapConsumptionService {
  /**
   * Obtiene los períodos de una orden
   * @param {string} factory - Código de fábrica
   * @param {string} prodLine - Línea de producción
   * @param {string} orderNumber - Número de orden
   * @returns {Promise<Array>} - Array de períodos
   */
  async getOrderPeriods(factory, prodLine, orderNumber) {
    const response = await api.get('/api/sap/period/list', {
      headers: {
        'Content-Type': 'application/json',
        'Factory': factory,
        'ProdLine': prodLine,
        'OrderNumber': orderNumber
      }
    })
    
    const periods = unwrap(response)
    // Asegurar que siempre retornamos un array
    return Array.isArray(periods) ? periods : []
  }

  /**
   * Obtiene los consumos de un período específico
   * @param {number} periodID - ID del período
   * @returns {Promise<Array>} - Array de consumos
   */
  async getPeriodConsumptions(periodID) {
    const queryParams = new URLSearchParams({ periodid: periodID.toString() })
    const response = await api.get(`/api/sap/period/consumption/list?${queryParams.toString()}`)
    
    const consumptions = unwrap(response)
    // Asegurar que siempre retornamos un array
    return Array.isArray(consumptions) ? consumptions : []
  }

  /**
   * Obtiene períodos con consumos por dosificador/tolva (API periodshopper)
   * @param {string} factory - Código de fábrica
   * @param {string} prodLine - Línea de producción
   * @param {string} orderNumber - Número de orden
   * @returns {Promise<Array>} - Array de períodos con Consumptions
   */
  async getHopperConsumptionPeriods(factory, prodLine, orderNumber) {
    const response = await api.get('/api/sap/consumption/periodshopper', {
      headers: {
        'Factory': factory,
        'ProdLine': prodLine,
        'OrderNumber': orderNumber
      }
    })
    const periods = unwrap(response)
    return Array.isArray(periods) ? periods : []
  }

  /**
   * Obtiene consumos usando el endpoint fallback (sin período específico)
   * @param {string} factory - Código de fábrica
   * @param {string} prodLine - Línea de producción
   * @param {string} orderNumber - Número de orden
   * @returns {Promise<Array>} - Array de consumos
   */
  async getConsumptionsFallback(factory, prodLine, orderNumber) {
    const response = await api.get('/api/sap/consumption/periodscoms', {
      headers: {
        'Factory': factory,
        'ProdLine': prodLine,
        'OrderNumber': orderNumber
      }
    })
    
    const consumptions = unwrap(response)
    return Array.isArray(consumptions) ? consumptions : []
  }

  /**
   * Calcula los consumos de un período
   * @param {number} periodID - ID del período
   * @returns {Promise<Object>} - Período actualizado con consumos calculados
   */
  async calculatePeriodConsumptions(periodID) {
    const queryParams = new URLSearchParams({ periodid: periodID.toString() })
    const response = await api.post(`/api/sap/period/consumption/calculate?${queryParams.toString()}`, null)
    
    return unwrap(response)
  }

  /**
   * Envía un período a SAP
   * @param {number} periodID - ID del período
   * @returns {Promise<Object>} - Período actualizado con estado SENT_TO_SAP
   */
  async sendPeriodToSAP(periodID) {
    const response = await api.post('/api/sap/period/consumption/sendToSAP', null, {
      headers: { 
        periodid: periodID.toString(),
        'Content-Type': 'application/json'
      }
    })
    
    return unwrap(response)
  }

  /**
   * Añade un consumo a un período
   * @param {number} periodID - ID del período
   * @param {string} componentSapCode - Código SAP del componente
   * @param {string} dosingUnit - Unidad de dosificación
   * @param {string} hopper - Tolva
   * @returns {Promise<void>} - No retorna datos (solo mensaje de éxito)
   */
  async addConsumption(periodID, componentSapCode, dosingUnit, hopper) {
    const requestData = {
      periodid: Number(periodID),
      componentsapcode: componentSapCode.trim(),
      dosingunit: dosingUnit.trim(),
      hopper: hopper.trim()
    }
    
    await api.post('/api/sap/period/consumption/add', requestData)
    // No retornamos nada porque el endpoint solo devuelve { message: "..." }
  }

  /**
   * Elimina un consumo de un período
   * @param {number} periodID - ID del período
   * @param {string} dosingUnit - Unidad de dosificación
   * @param {string} hopper - Tolva
   * @returns {Promise<void>} - No retorna datos (solo mensaje de éxito)
   */
  async removeConsumption(periodID, dosingUnit, hopper) {
    await api.post('/api/sap/period/consumption/remove', {
      periodid: periodID,
      dosingunit: dosingUnit,
      hopper: hopper
    })
    // No retornamos nada porque el endpoint solo devuelve { message: "..." }
  }

  /**
   * Actualiza un consumo en un período
   * @param {number} periodID - ID del período
   * @param {string} componentSapCode - Nuevo código SAP del componente
   * @param {string} dosingUnit - Nueva unidad de dosificación
   * @param {string} hopper - Nueva tolva
   * @param {string} originalDosingUnit - Unidad de dosificación original (para encontrar el consumo)
   * @param {string} originalHopper - Tolva original (para encontrar el consumo)
   * @param {string} createdAt - Fecha/hora de creación (formato: "YYYY-MM-DDTHH:mm" o ISO string)
   * @param {string} updatedAt - Fecha/hora de actualización (formato: "YYYY-MM-DDTHH:mm" o ISO string)
   * @returns {Promise<void>} - No retorna datos (solo mensaje de éxito)
   */
  async updateConsumption(periodID, componentSapCode, dosingUnit, hopper, originalDosingUnit = null, originalHopper = null, createdAt = null, updatedAt = null) {
    const payload = {
      periodid: periodID,
      componentsapcode: componentSapCode.trim(),
      dosingunit: dosingUnit.trim(),
      hopper: hopper.trim()
    }
    
    // Si se proporcionan valores originales, incluirlos
    if (originalDosingUnit) {
      payload.originaldosingunit = originalDosingUnit.trim()
    }
    if (originalHopper) {
      payload.originalhopper = originalHopper.trim()
    }
    
    // Convertir fechas de datetime-local a ISO string si se proporcionan
    if (createdAt) {
      // Si viene en formato datetime-local (YYYY-MM-DDTHH:mm), convertir a ISO
      if (createdAt.includes('T') && !createdAt.includes('Z') && !createdAt.includes('+')) {
        payload.createdat = new Date(createdAt).toISOString()
      } else {
        payload.createdat = createdAt
      }
    }
    
    if (updatedAt) {
      // Si viene en formato datetime-local (YYYY-MM-DDTHH:mm), convertir a ISO
      if (updatedAt.includes('T') && !updatedAt.includes('Z') && !updatedAt.includes('+')) {
        payload.updatedat = new Date(updatedAt).toISOString()
      } else {
        payload.updatedat = updatedAt
      }
    }
    
    const response =     await api.post('/api/sap/period/consumption/update', payload)
    // El endpoint ahora retorna { message: "...", consumptions: [...] }
    return unwrap(response)
  }

  /**
   * Actualiza la fecha de fin de un período
   * @param {number} periodID - ID del período
   * @param {Date|string} newPeriodEnd - Nueva fecha de fin (ISO string o Date)
   * @returns {Promise<Object>} - Período actualizado
   */
  async updatePeriodEnd(periodID, newPeriodEnd) {
    const payload = {
      periodid: periodID,
      newperiodend: newPeriodEnd instanceof Date ? newPeriodEnd.toISOString() : newPeriodEnd
    }
    const response = await api.post('/api/sap/period/updateEnd', payload)
    return unwrap(response)
  }

  /**
   * Finaliza un período y crea el siguiente
   * @param {number} periodID - ID del período a finalizar
   * @returns {Promise<Object>} - Período finalizado con información del siguiente período
   */
  async finalizePeriodAndNext(periodID) {
    const response = await api.post('/api/sap/period/finalizeAndNext', null, {
      headers: { periodid: periodID.toString() }
    })
    
    return unwrap(response)
  }

  /**
   * Inicia una orden de producción (crea el período 1 automáticamente)
   * Regla de negocio 1: El período 1 toma la fecha/hora de inicio de OF
   * @param {string} factory - Código de fábrica
   * @param {string} prodLine - Línea de producción
   * @param {string} orderNumber - Número de orden
   * @param {Date|string|null} startedAt - Fecha/hora de inicio OF (opcional; si no se pasa, usa la actual)
   * @returns {Promise<Object>} - Orden actualizada
   */
  async startProductionOrder(factory, prodLine, orderNumber, startedAt = null) {
    const headers = {
      'Factory': factory,
      'ProdLine': prodLine,
      'OrderNumber': orderNumber
    }
    if (startedAt) {
      headers['StartedAt'] = startedAt instanceof Date ? startedAt.toISOString() : startedAt
    }
    const response = await api.post('/api/sap/order/start', null, { headers })
    
    return unwrap(response)
  }

  /**
   * Finaliza una orden de producción (cierra OF y último período activo)
   * @param {string} factory - Código de fábrica
   * @param {string} prodLine - Línea de producción
   * @param {string} orderNumber - Número de orden
   * @returns {Promise<Object>} - Respuesta del backend
   */
  async finishProductionOrder(factory, prodLine, orderNumber) {
    const headers = {
      'Factory': factory,
      'ProdLine': prodLine,
      'OrderNumber': orderNumber
    }
    const response = await api.post('/api/sap/order/finish', null, { headers })

    return unwrap(response)
  }

  /**
   * Actualiza las fechas de inicio y fin de una orden de producción
   * @param {string} factory - Código de fábrica
   * @param {string} prodLine - Línea de producción
   * @param {string} orderNumber - Número de orden
   * @param {Date|string|null} startDate - Fecha de inicio (ISO string o Date object)
   * @param {Date|string|null} endDate - Fecha de fin (ISO string o Date object)
   * @param {{
   *   orderSnapshot?: Record<string, unknown>|null
   *   fillStartedAtFromSnapshot?: boolean
   *   fillFinishedAtFromSnapshot?: boolean
   *   minimumFinishedAtFromPeriods?: Date|null
   * }} [options] - Snapshot opcional para fechas que no envías; clamps para cumplir validación backend (ej. FinishedAt ≥ inicio último período)
   * @returns {Promise<Object>} - Respuesta de actualización
   */
  async updateOrderDates(factory, prodLine, orderNumber, startDate = null, endDate = null, options = {}) {
    const orderSnapshot = options?.orderSnapshot
    const fillStartedAtFromSnapshot = options?.fillStartedAtFromSnapshot !== false
    const fillFinishedAtFromSnapshot = options?.fillFinishedAtFromSnapshot !== false
    const minimumFinishedAtFromPeriods = options?.minimumFinishedAtFromPeriods

    let effectiveStart = startDate
    let effectiveEnd = endDate

    if (orderSnapshot) {
      if (!effectiveStart && fillStartedAtFromSnapshot) {
        const d = parseOrderStartedAt(orderSnapshot)
        if (d) effectiveStart = d
      }
      if (!effectiveEnd && fillFinishedAtFromSnapshot) {
        const d = parseOrderFinishedAt(orderSnapshot)
        if (d) effectiveEnd = d
      }
    }

    const minEnd =
      minimumFinishedAtFromPeriods instanceof Date &&
      !Number.isNaN(minimumFinishedAtFromPeriods.getTime())
        ? minimumFinishedAtFromPeriods
        : null
    if (minEnd) {
      const endMs =
        effectiveEnd != null && effectiveEnd !== ''
          ? effectiveEnd instanceof Date
            ? effectiveEnd.getTime()
            : new Date(effectiveEnd).getTime()
          : NaN
      if (effectiveEnd == null || effectiveEnd === '' || Number.isNaN(endMs) || endMs < minEnd.getTime()) {
        effectiveEnd = minEnd
      }
    }

    const headers = {
      'Content-Type': 'application/json',
      'Factory': factory,
      'ProdLine': prodLine,
      'OrderNumber': orderNumber
    }

    // Convertir fechas a ISO string si son Date objects
    if (effectiveStart) {
      headers['StartedAt'] =
        effectiveStart instanceof Date ? effectiveStart.toISOString() : effectiveStart
    }
    if (effectiveEnd) {
      headers['FinishedAt'] =
        effectiveEnd instanceof Date ? effectiveEnd.toISOString() : effectiveEnd
    }

    const response = await api.get('/api/sap/orders/update', { headers })
    
    return unwrap(response)
  }

  /**
   * Obtiene la receta de una orden
   * @param {string} factory - Código de fábrica
   * @param {string} prodLine - Línea de producción
   * @param {string} orderNumber - Número de orden
   * @param {boolean} fromSAP - Si es true, carga desde SAP (default: false)
   * @returns {Promise<Object>} - Receta con componentes
   */
  async getOrderRecipe(factory, prodLine, orderNumber, fromSAP = false) {
    const headers = {
      'Content-Type': 'application/json',
      'SapOrderCode': orderNumber, // Mantener SapOrderCode para compatibilidad
      'SapRequest': fromSAP ? 'true' : 'false',
      'Factory': factory,
      'ProdLine': prodLine
    }
    
    const response = await api.get('/api/sap/order/recipe', { headers })
    
    return unwrap(response)
  }

  /**
   * Ajusta la cantidad de un consumo específico (tolva/dosificador)
   * @param {number} periodID - ID del período
   * @param {string} dosingUnit - Unidad de dosificación
   * @param {string} hopper - Tolva
   * @param {number} adjustedQuantity - Nueva cantidad ajustada
   * @returns {Promise<Object>} - Período actualizado
   */
  async adjustHopperConsumption(periodID, dosingUnit, hopper, adjustedQuantity) {
    const response = await api.post('/api/sap/period/consumption/adjusthopper', {
      periodid: periodID,
      dosingunit: dosingUnit,
      hopper: hopper,
      adjustedquantity: adjustedQuantity
    })
    
    return unwrap(response)
  }
}

// Exportar instancia singleton
export const sapConsumptionService = new SapConsumptionService()
export default sapConsumptionService
